import React from 'react';
import { useChatTopicsContext } from '../../common/chat/topics/ChatTopicsContext';
import ChatTargetSidebarToolbar from '../../common/chat/sidepanel/ChatTargetSidebarToolbar';
import ChatTargetSidebarLoading from '../../common/chat/sidepanel/ChatTargetSidebarLoading';
import ChatTargetListRow from '../../common/chat/sidepanel/ChatTargetListRow';
import ChatMentionTaskNotice from '../../common/chat/mentions/ChatMentionTaskNotice';
import AiFluidMark from '../../common/chat/shared/AiFluidMark';
import ChatFollowupNotificationMenu from '../../common/chat/followups/ChatFollowupNotificationMenu';
import { resolveChatFollowupBadgeToneClass } from '../../common/chat/followups/chatFollowupApi';
import EventFlyerMedia from '../../common/EventFlyerMedia';
import {
  EVENT_TARGET_ROW_CLASSES,
  buildFlyerUrl,
  pushViewState,
} from './eventChatPageHelpers';
import {
  resolveEventSidebarPreview as resolveSidebarPreview,
} from '../../common/chat/timeline/eventMessageFormatUtils';
import {
  isUnifiedEventGroupChatRow,
} from '../../common/chat/eventlist/eventThreadMetaUtils';
import { renderChatSearchHighlight } from '../../common/chat/search/chatSearchHighlight';
import { formatItemCount } from '../../../utils/datetime';

export default function EventChatSidebar({
  openTargetSearchModal,
  searchSummaryLabel,
  searchToolbarChips,
  assigneeQuickToggles,
  messageSearchQuery,
  onClearSearch,
  resultCount,
  hasMoreResults,
  isSearchLoadingMore,
  setEventListNode,
  handleEventListScroll,
  isBootLoading,
  isTargetFilterMode,
  isAiTargetSelected,
  openAiInbox,
  followupBadgeCount,
  followupSummary,
  displayedEvents,
  resolveEventUnread,
  isEventChatAreaOpen,
  selectedEventId,
  isAdminAssignedToEvent,
  pendingMentionTargets,
  resolvingMentionTargetId,
  loadingMentionResolutionTargetKey,
  openMentionResolution,
  resolveMentionTarget,
  sidebarPressRef,
  clearSidebarContextMenuBlock,
  clearSidebarLongPress,
  handleSidebarTouchStart,
  openSettingsModal,
  isNarrow,
  setMobileView,
  showNotes,
  handleSelectEvent,
  formatEventListTimestamp,
  isLoadingEvents,
  hasMoreEvents,
  setLoadMoreNode,
  toastError,
  toastSuccess,
}) {
  const followupBadgeToneClass =
    followupBadgeCount > 0 ? resolveChatFollowupBadgeToneClass(followupSummary) : '';
  const chatTopics = useChatTopicsContext();
  const [followupNotificationMenu, setFollowupNotificationMenu] = React.useState(null);
  const closeFollowupNotificationMenu = React.useCallback(() => {
    setFollowupNotificationMenu(null);
  }, []);
  const openFollowupNotificationMenuAt = React.useCallback((clientX, clientY) => {
    setFollowupNotificationMenu({ x: clientX, y: clientY });
  }, []);
  const blockFollowupContextMenu = React.useCallback(() => {
    clearSidebarContextMenuBlock();
    sidebarPressRef.current.suppressContextMenu = true;
    sidebarPressRef.current.contextMenuTimer = window.setTimeout(() => {
      sidebarPressRef.current.suppressContextMenu = false;
      sidebarPressRef.current.contextMenuTimer = null;
    }, 700);
  }, [clearSidebarContextMenuBlock, sidebarPressRef]);
  const handleAiTargetTouchStart = React.useCallback((event) => {
    sidebarPressRef.current.suppressClick = false;
    clearSidebarContextMenuBlock();
    clearSidebarLongPress();
    if (!isNarrow) return;
    const touch = event.touches?.[0];
    const clientX = touch?.clientX ?? window.innerWidth / 2;
    const clientY = touch?.clientY ?? 72;
    sidebarPressRef.current.timer = window.setTimeout(() => {
      sidebarPressRef.current.timer = null;
      sidebarPressRef.current.suppressClick = true;
      blockFollowupContextMenu();
      openFollowupNotificationMenuAt(clientX, clientY);
    }, 520);
  }, [
    blockFollowupContextMenu,
    clearSidebarContextMenuBlock,
    clearSidebarLongPress,
    isNarrow,
    openFollowupNotificationMenuAt,
    sidebarPressRef,
  ]);

  return (
    <>
      <div className="event-sidebar chat-target-sidebar">
        <ChatTargetSidebarToolbar
          onOpenSearch={openTargetSearchModal}
          summary={isTargetFilterMode ? '' : searchSummaryLabel}
          chips={searchToolbarChips}
          toggles={assigneeQuickToggles}
        />

      <ul
        className="event-list"
        ref={setEventListNode}
        onScroll={handleEventListScroll}
        aria-busy={isBootLoading ? 'true' : 'false'}
      >
        {/* AI行（またはフィルタ時の検索ヘッダー）は読み込み中でも最初から固定表示。
            その下の実リストだけがスケルトン表示になる。 */}
        {isTargetFilterMode ? (
          <li className="event-list__result-header">
            <span className="event-list__result-count">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              検索結果 {formatItemCount(resultCount || 0)}{hasMoreResults ? '+' : ''}
            </span>
            <button
              type="button"
              className="event-list__result-clear"
              onClick={onClearSearch}
            >
              <i className="fa-solid fa-xmark" aria-hidden="true" />
              <span>解除</span>
            </button>
          </li>
        ) : (
          <ChatTargetListRow
            classNames={{
              ...EVENT_TARGET_ROW_CLASSES,
              right: `${EVENT_TARGET_ROW_CLASSES.right} chat-followup-target-row__side`,
            }}
            className={`chat-followup-target-row__eventItem${isAiTargetSelected ? ' is-active' : ''}`}
            onClick={() => {
              if (sidebarPressRef.current.suppressClick) {
                sidebarPressRef.current.suppressClick = false;
                return;
              }
              openAiInbox();
            }}
            onContextMenu={(event) => {
              if (sidebarPressRef.current.suppressContextMenu) {
                event.preventDefault();
                event.stopPropagation();
                clearSidebarContextMenuBlock();
                return;
              }
              event.preventDefault();
              event.stopPropagation();
              sidebarPressRef.current.suppressClick = true;
              openFollowupNotificationMenuAt(event.clientX, event.clientY);
              setTimeout(() => {
                sidebarPressRef.current.suppressClick = false;
              }, 0);
            }}
            onTouchStart={handleAiTargetTouchStart}
            onTouchEnd={clearSidebarLongPress}
            onTouchCancel={clearSidebarLongPress}
            onTouchMove={clearSidebarLongPress}
            ariaCurrent={isAiTargetSelected ? 'true' : undefined}
            avatar={(
              <AiFluidMark variant="sidebar" className="chat-followup-target-row__eventAiMark" />
            )}
            title="CasticartAI"
            badge={followupBadgeCount > 0 ? (followupBadgeCount > 99 ? '99+' : followupBadgeCount) : null}
            badgeClassName={followupBadgeToneClass}
          />
        )}
        {isBootLoading ? (
          <ChatTargetSidebarLoading
            variant="event"
            count={4}
            showLoadMore={!isTargetFilterMode}
            loadMoreClassName="event-list__loadmore"
            loadMoreButtonClassName="event-list__loadmore-btn"
          />
        ) : (
          <>
            {displayedEvents.length === 0 && (
              <li className="event-list__empty-hint">
                {isTargetFilterMode
                  ? '一致するチャットはありません。条件を変えるか「解除」してください。'
                  : '該当するチャットがありません。'}
              </li>
            )}
            {displayedEvents.map((evt) => {
              if (!evt) return null;
              const rowKey = evt.search_hit_message_id
                ? `${evt.event_uuid}-${evt.search_hit_message_id}`
                : String(evt.event_uuid || '');
              let unread = resolveEventUnread(evt);
              if (isEventChatAreaOpen && evt.event_uuid === selectedEventId) unread = 0;
              const isNotifyMuted = !!evt.admin_muted;
              const isAssigned = isAdminAssignedToEvent(evt);
              const isGroupUnifiedRow = isUnifiedEventGroupChatRow(evt);
              const unreadBadgeToneClass = isAssigned ? 'badge-tone--alert' : 'badge-tone--ok';
              const flyerUrl = evt?.flyer_front_url
                ? buildFlyerUrl(evt.flyer_front_url, 'card')
                : '';
              const previewText = resolveSidebarPreview(evt);
              const isMessageHit = evt.search_result_type === 'message-hit';
              // 検索/絞り込み中は「未対応メンション」の受信箱ワークフローを結果に混ぜない。
              const mentionTask = isTargetFilterMode
                ? null
                : pendingMentionTargets[String(evt.event_uuid || '').trim()] || null;

              return (
                <ChatTargetListRow
                  topicScope="event" topicConversationId={evt.event_uuid} hasTopics={evt.has_topics || (String(evt.event_uuid) === chatTopics?.conversationId && chatTopics.rows.length > 0)}
                  onTopicSelect={(topic) => { chatTopics?.selectTargetTopic(evt.event_uuid, topic); handleSelectEvent(evt.event_uuid); }}
                  key={rowKey}
                  classNames={EVENT_TARGET_ROW_CLASSES}
                  className={`${!isAiTargetSelected && evt.event_uuid === selectedEventId ? 'is-active' : ''}${
                    mentionTask?.count > 0 ? ' event-target-row--has-mention' : ''
                  }`}
                  onClick={() => {
                    const hitMessageId = String(evt?.search_hit_message_id || '').trim();
                    if (sidebarPressRef.current.suppressClick) {
                      sidebarPressRef.current.suppressClick = false;
                      return;
                    }
                    if (!isAiTargetSelected && evt.event_uuid === selectedEventId && !hitMessageId) {
                      if (isNarrow) {
                        setMobileView('chat');
                        pushViewState(selectedEventId, showNotes ? 'notes-list' : 'chat');
                      }
                      return;
                    }
                    handleSelectEvent(evt.event_uuid, {
                      clearUrl: true,
                      anchorMessageId: hitMessageId,
                    });
                  }}
                  onContextMenu={(event) => {
                    if (sidebarPressRef.current.suppressContextMenu) {
                      event.preventDefault();
                      event.stopPropagation();
                      clearSidebarContextMenuBlock();
                      return;
                    }
                    event.preventDefault();
                    sidebarPressRef.current.suppressClick = true;
                    openSettingsModal(evt, { selectOnOpen: false });
                    setTimeout(() => {
                      sidebarPressRef.current.suppressClick = false;
                    }, 0);
                  }}
                  onTouchStart={() => {
                    sidebarPressRef.current.suppressClick = false;
                    clearSidebarContextMenuBlock();
                    handleSidebarTouchStart(evt);
                  }}
                  onTouchEnd={clearSidebarLongPress}
                  onTouchCancel={clearSidebarLongPress}
                  onTouchMove={clearSidebarLongPress}
                  ariaCurrent={!isAiTargetSelected && evt.event_uuid === selectedEventId ? 'true' : undefined}
                  avatar={
                    <>
                      <EventFlyerMedia
                        event={evt}
                        imageUrl={flyerUrl}
                        imageAlt={`${evt.name || 'イベント'}のフライヤー`}
                        sizeVariant="small"
                        borderRadius={10}
                        placeholderClassName="event-list__flyer-placeholder"
                        imageClassName="event-list__flyer"
                        hasImage={Boolean(flyerUrl)}
                        onImageError={(target) => { target.style.display = 'none'; }}
                      />
                      {isNotifyMuted && (
                        <span className="event-list__mute-badge" aria-hidden="true" title="通知オフ">
                          <i className="fa-solid fa-bell-slash" aria-hidden="true"></i>
                        </span>
                      )}
                    </>
                  }
                  title={evt.name}
                  previewNode={
                    isMessageHit ? (
                      <span className="event-list__last-preview">
                        {evt.search_hit_sender_name ? (
                          <span className="event-list__hit-sender">{evt.search_hit_sender_name}：</span>
                        ) : null}
                        {renderChatSearchHighlight(previewText, messageSearchQuery)}
                      </span>
                    ) : (
                      <>
                        {!isGroupUnifiedRow && evt.performance_type ? (
                          <span className="event-list__performance">{evt.performance_type}</span>
                        ) : null}
                        {previewText ? (
                          <span className="event-list__last-preview">{previewText}</span>
                        ) : null}
                      </>
                    )
                  }
                  belowContent={
                    mentionTask?.count > 0 ? (
                      <ChatMentionTaskNotice
                        count={mentionTask.count}
                        disabled={
                          resolvingMentionTargetId === String(evt.event_uuid || '').trim() ||
                          loadingMentionResolutionTargetKey === `event:${String(evt.event_uuid || '').trim()}`
                        }
                        onResolve={() =>
                          openMentionResolution({
                            scope: 'event',
                            targetId: evt.event_uuid,
                            count: mentionTask.count,
                            title: evt.name ? `${evt.name} の未対応メンション` : '未対応メンション',
                            resolveTarget: resolveMentionTarget,
                          })
                        }
                        onOpen={() =>
                          handleSelectEvent(evt.event_uuid, {
                            clearUrl: true,
                            anchorMessageId: String(mentionTask.messageId || '').trim(),
                          })
                        }
                      />
                    ) : null
                  }
                  time={formatEventListTimestamp(evt.last_message_time)}
                  badge={!isMessageHit && unread > 0 ? (unread > 99 ? '99+' : unread) : null}
                  badgeClassName={!isMessageHit && unread > 0 ? unreadBadgeToneClass : ''}
                />
              );
            })}

            {!isTargetFilterMode && (
              <li
                className="event-list__loadmore"
                ref={setLoadMoreNode}
                aria-busy={isLoadingEvents ? 'true' : 'false'}
              >
                {isLoadingEvents ? (
                  <span role="status" aria-live="polite">読み込み中…</span>
                ) : hasMoreEvents ? (
                  'スクロールで自動取得'
                ) : (
                  ''
                )}
              </li>
            )}
            {isTargetFilterMode && displayedEvents.length > 0 && (hasMoreResults || isSearchLoadingMore) ? (
              <li
                className="event-list__loadmore"
                aria-busy={isSearchLoadingMore ? 'true' : 'false'}
              >
                {isSearchLoadingMore ? (
                  <span role="status" aria-live="polite">過去の検索結果を読み込み中…</span>
                ) : (
                  'スクロールで過去の結果を追加'
                )}
              </li>
            ) : null}
          </>
        )}
        </ul>
      </div>
      <ChatFollowupNotificationMenu
        menu={followupNotificationMenu}
        onClose={closeFollowupNotificationMenu}
        toastError={toastError}
        toastSuccess={toastSuccess}
      />
    </>
  );
}
